import { useRef } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Slider from "react-slick";
import { Link as RouterLink } from "react-router-dom";
import TelegramIcon from "@mui/icons-material/Telegram";
import GroupsIcon from "@mui/icons-material/Groups";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import RuleIcon from "@mui/icons-material/Rule";
import PublicIcon from "@mui/icons-material/Public";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StarIcon from "@mui/icons-material/Star";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  MAIN_TELEGRAM_URL,
  TELEGRAM_GROUPS,
  TelegramGroup,
} from "utils/telegramGroups";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./styles.scss";

type CardItem = {
  icon: React.ReactElement;
  title: string;
  descr: string;
  href: string;
  external?: boolean;
};

const kindLabel: Record<TelegramGroup["kind"], string> = {
  main: "Hauptgruppe",
  offtop: "Off-Topic",
  city: "Stadt",
  state: "Bundesland",
};

const kindIcon: Record<TelegramGroup["kind"], React.ReactElement> = {
  main: <StarIcon fontSize="small" />,
  offtop: <ChatBubbleOutlineIcon fontSize="small" />,
  city: <LocationCityIcon fontSize="small" />,
  state: <PublicIcon fontSize="small" />,
};

function buildItems(g: TelegramGroup): CardItem[] {
  return [
    {
      icon: <TelegramIcon />,
      title: "Telegram-Chat",
      descr: g.username,
      href: g.url,
      external: true,
    },
    {
      icon: <PublicIcon />,
      title: "Alle Regionen",
      descr: "Stadt & Bundesland",
      href: "#communities",
    },
    {
      icon: <WorkOutlineIcon />,
      title: "Jobs",
      descr: "Offene Stellen",
      href: "https://innovaforge.de/jobs/",
      external: true,
    },
    {
      icon: <RuleIcon />,
      title: "Regeln",
      descr: "DE & EN",
      href: "/network-it-rules",
    },
  ];
}

function ItemRow({ item }: { item: CardItem }) {
  const content = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        py: 1,
        textDecoration: "none",
        color: "#2c2936",
        transition: "transform 200ms ease",
        "&:hover": { transform: "translateX(4px)" },
        "&:hover .row-arrow": { color: "#583dff" },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          background:
            "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
          color: "#fff",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 18px -8px rgba(88,61,255,0.55)",
        }}
      >
        {item.icon}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#2c2936",
            }}
          >
            {item.title}
          </Typography>
          <ArrowForwardIcon
            className="row-arrow"
            sx={{
              fontSize: 18,
              color: "#cb97ff",
              transition: "color 200ms ease",
            }}
          />
        </Stack>
        <Typography
          sx={{
            fontSize: 13,
            color: "#5d5870",
            mt: 0.25,
            wordBreak: "break-all",
          }}
        >
          {item.descr}
        </Typography>
      </Box>
    </Stack>
  );

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", display: "block" }}
      >
        {content}
      </a>
    );
  }
  if (item.href.startsWith("#")) {
    return (
      <a href={item.href} style={{ textDecoration: "none", display: "block" }}>
        {content}
      </a>
    );
  }
  return (
    <RouterLink
      to={item.href}
      style={{ textDecoration: "none", display: "block" }}
    >
      {content}
    </RouterLink>
  );
}

function Hero() {
  const sliderRef = useRef<Slider | null>(null);

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 5000,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: true,
    pauseOnHover: true,
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, md: 12 },
        background:
          "radial-gradient(1100px 600px at 80% -10%, rgba(203,151,255,0.35) 0%, rgba(88,61,255,0.08) 45%, transparent 70%), linear-gradient(180deg, #fafafa 0%, #f1eef8 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
            gap: { xs: 6, md: 6 },
            alignItems: "center",
          }}
        >
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupsIcon sx={{ color: "#583dff" }} />
              <Typography
                variant="overline"
                sx={{ letterSpacing: 2, color: "#583dff", fontWeight: 700 }}
              >
                IT-Community in Deutschland
              </Typography>
            </Stack>
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: 36, sm: 48, md: 58 },
                lineHeight: 1.05,
                color: "#2c2936",
              }}
            >
              Vernetze dich mit{" "}
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                NETWORKIT
              </Box>{" "}
              auf Telegram.
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 16, md: 18 },
                lineHeight: 1.6,
                color: "#4a4658",
                maxWidth: 540,
              }}
            >
              Lokale Treffen, Wissensaustausch und neue Kontakte in der
              IT-Community. Tritt der Hauptgruppe bei oder finde den Chat
              deiner Stadt.
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ pt: 1 }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<TelegramIcon />}
                href={MAIN_TELEGRAM_URL}
                target="_blank"
                rel="noopener"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: 16,
                  background:
                    "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
                  boxShadow: "0 12px 30px -10px rgba(88,61,255,0.55)",
                  "&:hover": {
                    background:
                      "linear-gradient(131.35deg, #b97aff 7.53%, #4429ff 94.2%)",
                  },
                }}
              >
                Telegram beitreten
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#communities"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: 16,
                  color: "#583dff",
                  borderColor: "#cb97ff",
                  "&:hover": {
                    borderColor: "#583dff",
                    background: "rgba(88,61,255,0.04)",
                  },
                }}
              >
                Alle Gruppen
              </Button>
            </Stack>
          </Stack>

          <Box className="hero-slider" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                position: "relative",
                borderRadius: 6,
                overflow: "hidden",
                background:
                  "linear-gradient(111.68deg, rgba(255,255,255,0.85) 7.59%, rgba(255,255,255,0.65) 102.04%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.6)",
                boxShadow: "0 30px 60px -25px rgba(88,61,255,0.35)",
              }}
            >
              <Slider {...sliderSettings} ref={sliderRef}>
                {TELEGRAM_GROUPS.map((g) => (
                  <div key={g.username}>
                    <Box sx={{ p: { xs: 3, md: 4 } }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                            color: "#583dff",
                            background: "rgba(88,61,255,0.10)",
                            borderRadius: 999,
                            px: 1.25,
                            py: 0.5,
                          }}
                        >
                          {kindIcon[g.kind]}
                          {kindLabel[g.kind]}
                        </Box>
                      </Stack>
                      <Typography
                        component="h2"
                        lang="de"
                        sx={{
                          mt: 1.5,
                          fontSize: { xs: 22, md: 26 },
                          fontWeight: 900,
                          color: "#2c2936",
                          lineHeight: 1.15,
                          textTransform: "uppercase",
                          wordBreak: "break-word",
                          hyphens: "auto",
                        }}
                      >
                        {g.city}
                      </Typography>
                      <Box
                        sx={{
                          mt: 2,
                          mb: 1,
                          height: 2,
                          width: 56,
                          borderRadius: 2,
                          background:
                            "linear-gradient(90deg, #cb97ff 0%, #583dff 100%)",
                        }}
                      />

                      <Stack divider={<Box sx={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }} />} spacing={0}>
                        {buildItems(g).map((item) => (
                          <ItemRow key={item.title} item={item} />
                        ))}
                      </Stack>

                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<TelegramIcon />}
                        href={g.url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                          mt: 2.5,
                          py: 1.25,
                          fontWeight: 700,
                          textTransform: "none",
                          fontSize: 15,
                          background:
                            "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
                          boxShadow:
                            "0 12px 30px -10px rgba(88,61,255,0.55)",
                          "&:hover": {
                            background:
                              "linear-gradient(131.35deg, #b97aff 7.53%, #4429ff 94.2%)",
                          },
                        }}
                      >
                        Beitreten
                      </Button>
                    </Box>
                  </div>
                ))}
              </Slider>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 2.5 }}
            >
              <IconButton
                aria-label="Vorherige Gruppe"
                onClick={() => sliderRef.current?.slickPrev()}
                sx={{
                  width: 44,
                  height: 44,
                  background: "#fff",
                  border: "1px solid rgba(88,61,255,0.15)",
                  color: "#583dff",
                  boxShadow: "0 8px 18px -8px rgba(88,61,255,0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
                    color: "#fff",
                    borderColor: "transparent",
                  },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                aria-label="Nächste Gruppe"
                onClick={() => sliderRef.current?.slickNext()}
                sx={{
                  width: 44,
                  height: 44,
                  background: "#fff",
                  border: "1px solid rgba(88,61,255,0.15)",
                  color: "#583dff",
                  boxShadow: "0 8px 18px -8px rgba(88,61,255,0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(131.35deg, #cb97ff 7.53%, #583dff 94.2%)",
                    color: "#fff",
                    borderColor: "transparent",
                  },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero;
